/* eslint-disable react/jsx-no-bind */
import {
  Settings,
  Close,
  LocationOn,
  Schedule,
  LocalShipping,
  Person,
  Luggage,
  Work,
  BusinessCenter,
} from '@mui/icons-material';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem as DropdownItem,
  Paper,
  IconButton,
  Menu,
  CardContent,
  Card,
  TablePagination,
  Modal,
  Typography,
  useTheme,
  Divider,
  Grid,
  Chip,
  Button,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  LocalizationProvider,
  DatePicker,
  DateTimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';

import { tokens } from '../../theme';
import { formatToIST, formatToISTCustom, formatDateToUTC } from '../../utils/dateFormatter';
import parcelWeightService from '../ParcelWeights/parcelWeights';

import tripService from './tripService';

const TripManagement = () => {
  const [apiResponse, setApiResponse] = useState({
    status: false,
    message: '',
    data: {
      result: [],
      total_records: 0,
      page: 1,
      page_size: 10,
    },
    status_code: 0,
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const isFetchingRef = useRef(false);

  // Create trip form state - matching backend API structure
  const [newTrip, setNewTrip] = useState({
    userId: '',
    trip_count: '',
    source: '',
    destination: '',
    source_name: '',
    dest_name: '',
    start_date: null,
    end_date: null,
    route: '',
    vehicle_details: 0,
    pkg_weight: 0,
    pkg_width: 0,
    pkg_height: 0,
    pkg_depth: 0,
    parcel_preference: 'OTR',
    other_preference: '',
  });

  const [selectedCapacity, setSelectedCapacity] = useState('');
  const [packageWeights, setPackageWeights] = useState([]);

  const [tripErrors, setTripErrors] = useState({
    userId: '',
    source: '',
    destination: '',
    source_name: '',
    dest_name: '',
    start_date: '',
    end_date: '',
  });

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'CRT', label: 'Created' },
    { value: 'ASG', label: 'Assigned' },
    { value: 'INT', label: 'In Transit' },
    { value: 'DLD', label: 'Delivered' },
    { value: 'CLD', label: 'Canceled' },
    { value: 'RFP', label: 'Ready for Pickup' },
    { value: 'ICR', label: 'Incident Reported' },
    { value: 'NTY', label: 'Notify' },
  ];

  const tripData = apiResponse.data?.result || [];
  const totalRecords = apiResponse.data?.total_records || 0;

  // Pagination object extracted from API response
  const pagination = {
    total: totalRecords,
    page: apiResponse.data?.page || 1,
    pageSize: apiResponse.data?.page_size || rowsPerPage,
  };

  const fetchTrips = useCallback(
    async (
      pageNum = 1,
      pageSize = 10,
      status = '',
      fromDateParam = null,
      toDateParam = null,
      searchParam = '',
    ) => {
      // Prevent duplicate concurrent calls
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setLoading(true);
      try {
        const res = await tripService.fetchTrips(
          pageNum,
          pageSize,
          status,
          fromDateParam,
          toDateParam,
          searchParam,
        );
        setApiResponse(res.data);
      } catch (err) {
        toast.error('Error fetching trips:', err);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [],
  );

  useEffect(() => {
    // Initial fetch on mount and when page/rowsPerPage changes
    // Filters are applied only when "Search" button is clicked
    fetchTrips(page + 1, rowsPerPage, statusFilter, fromDate, toDate, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  useEffect(() => {
    if (createModalOpen) {
      parcelWeightService
        .fetchParcelWeights()
        .then((res) => {
          const list =
            res?.data?.data?.package_weights ??
            res?.data?.package_weights ??
            [];
          setPackageWeights(Array.isArray(list) ? list : []);
        })
        .catch(() => setPackageWeights([]));
    }
  }, [createModalOpen]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    // if (searchTimeout) {
    //   clearTimeout(searchTimeout);
    // }

    // setSearchTimeout(
    //   setTimeout(() => {
    //     setPage(0);
    //     fetchTrips(1, rowsPerPage, statusFilter, fromDate, toDate, value);
    //   }, 500)
    // );
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchTrips(1, rowsPerPage, statusFilter, fromDate, toDate, search);
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedTrip(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedTrip(null);
  };

  const handleCreateModalOpen = () => {
    setCreateModalOpen(true);
    setNewTrip({
      userId: '',
      trip_count: '',
      source: '',
      destination: '',
      source_name: '',
      dest_name: '',
      start_date: null,
      end_date: null,
      route: '',
      vehicle_details: 0,
      pkg_weight: 0,
      pkg_width: 0,
      pkg_height: 0,
      pkg_depth: 0,
      parcel_preference: 'OTR',
      other_preference: '',
    });
    setTripErrors({
      userId: '',
      source: '',
      destination: '',
      source_name: '',
      dest_name: '',
      start_date: '',
      end_date: '',
    });
  };

  const handleCreateModalClose = () => {
    setCreateModalOpen(false);
    setSelectedCapacity('');
    setNewTrip({
      userId: '',
      trip_count: '',
      source: '',
      destination: '',
      source_name: '',
      dest_name: '',
      start_date: null,
      end_date: null,
      route: '',
      vehicle_details: 0,
      pkg_weight: 0,
      pkg_width: 0,
      pkg_height: 0,
      pkg_depth: 0,
      parcel_preference: 'OTR',
      other_preference: '',
    });
    setTripErrors({
      userId: '',
      source: '',
      destination: '',
      source_name: '',
      dest_name: '',
      start_date: '',
      end_date: '',
    });
  };

  const validateTripForm = () => {
    const errors = {
      userId: '',
      source: '',
      destination: '',
      source_name: '',
      dest_name: '',
      start_date: '',
      end_date: '',
    };
    let isValid = true;

    if (!newTrip.userId) {
      errors.userId = 'User ID is required';
      isValid = false;
    }

    if (!newTrip.source.trim()) {
      errors.source = 'Source is required';
      isValid = false;
    }

    if (!newTrip.destination.trim()) {
      errors.destination = 'Destination is required';
      isValid = false;
    }

    if (!newTrip.source_name.trim()) {
      errors.source_name = 'Source name is required';
      isValid = false;
    }

    if (!newTrip.dest_name.trim()) {
      errors.dest_name = 'Destination name is required';
      isValid = false;
    }

    if (!newTrip.start_date) {
      errors.start_date = 'Start date and time is required';
      isValid = false;
    }

    if (!newTrip.end_date) {
      errors.end_date = 'End date and time is required';
      isValid = false;
    }

    if (newTrip.start_date && newTrip.end_date) {
      if (dayjs(newTrip.end_date).isBefore(dayjs(newTrip.start_date))) {
        errors.end_date = 'End date must be after start date';
        isValid = false;
      }
    }

    setTripErrors(errors);
    return isValid;
  };

  const handleCreateTrip = async () => {
    if (!validateTripForm()) {
      return;
    }

    setCreateLoading(true);
    try {
      // Format data to match backend API structure (snake_case)
      // API expects dates in "DD-MM-YYYY HH:mm" format (UTC)
      const tripPayload = {
        userId: newTrip.userId.trim(),
        trip_count: newTrip.trip_count || '1',
        source: newTrip.source.trim(),
        destination: newTrip.destination.trim(),
        source_name: newTrip.source_name.trim(),
        dest_name: newTrip.dest_name.trim(),
        start_date: formatDateToUTC(newTrip.start_date),
        end_date: formatDateToUTC(newTrip.end_date),
        route: newTrip.route || '',
        vehicle_details: newTrip.vehicle_details || 0,
        pkg_weight: newTrip.pkg_weight || 0,
        pkg_width: newTrip.pkg_width || 0,
        pkg_height: newTrip.pkg_height || 0,
        pkg_depth: newTrip.pkg_depth || 0,
        parcel_preference: newTrip.parcel_preference || 'OTR',
        other_preference: newTrip.other_preference || '',
      };

      const response = await tripService.createTrip(tripPayload);
      const message = response?.data?.message || 'Trip created successfully';
      toast.success(message);
      handleCreateModalClose();
      // Refresh trips list
      fetchTrips(page + 1, rowsPerPage, statusFilter, fromDate, toDate, search);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create trip');
    } finally {
      setCreateLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'delivered':
        return 'success';
      case 'in transit':
        return 'warning';
      case 'created':
        return 'info';
      case 'canceled':
        return 'error';
      case 'ready for pickup':
        return 'secondary';
      case 'assigned':
        return 'primary';
      default:
        return 'default';
    }
  };

  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter('');
    setFromDate(null);
    setToDate(null);
    setPage(0);
    // Fetch with reset values
    fetchTrips(1, rowsPerPage, '', null, null, '');
  };

  return (
    <>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          padding: 1,
          margin: 2,
          marginLeft: 4,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: 4,
              paddingTop: 2,
              paddingBottom: 0,
            }}
          >
            <h3>Trip Management</h3>
            <Button
              variant="contained"
              onClick={handleCreateModalOpen}
              sx={{
                bgcolor: colors.swiftcarry[100],
                '&:hover': {
                  bgcolor: colors.swiftcarry[100],
                },
                height: '40px',
                mr: 4,
              }}
            >
              Create Trip
            </Button>
          </Box>
          <Divider />
          <Box sx={{ padding: 4 }}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ marginBottom: 3 }}
            >
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Search by Trip ID or Phone Number"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={search}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  label="Filter by Status"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (selected === '' || !selected) {
                        return 'All Statuses';
                      }
                      const option = statusOptions.find(
                        (opt) => opt.value === selected,
                      );
                      return option ? option.label : selected;
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiSelect-select': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                      transform: 'translate(14px, 10px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                      transform: 'translate(14px, -9px) scale(0.75)',
                    },
                  }}
                >
                  {statusOptions.map((option) => (
                    <DropdownItem key={option.value} value={option.value}>
                      {option.label}
                    </DropdownItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DatePicker
                      label="From Date"
                      value={fromDate}
                      onChange={(newDate) => {
                        setFromDate(newDate);
                      }}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                            '& .MuiInputLabel-root': {
                              color: 'gray',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: colors.swiftcarry[100],
                            },
                          },
                        },
                      }}
                    />
                    <Typography sx={{ color: '#666', mx: 0.5 }}>—</Typography>
                    <DatePicker
                      label="To Date"
                      value={toDate}
                      onChange={(newDate) => {
                        setToDate(newDate);
                      }}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                            '& .MuiInputLabel-root': {
                              color: 'gray',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: colors.swiftcarry[100],
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    sx={{
                      bgcolor: colors.swiftcarry[100],
                      '&:hover': {
                        bgcolor: colors.swiftcarry[100],
                      },
                      height: '40px',
                      flex: 1,
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetFilters}
                    sx={{
                      borderColor: '#ccc',
                      color: '#666',
                      height: '40px',
                      flex: 1,
                      '&:hover': {
                        borderColor: colors.swiftcarry[100],
                        color: colors.swiftcarry[100],
                        backgroundColor: `${colors.swiftcarry[100]}10`,
                      },
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <TableContainer
              component={Paper}
              sx={{ position: 'relative', minHeight: 200 }}
            >
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Trip ID
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Source</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Destination
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Vehicle
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Package
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Package Type
                    </TableCell>
                    <TableCell
                      style={{
                        fontWeight: 'bold',
                        minWidth: 200,
                        width: 200,
                      }}
                    >
                      Dates
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Created By
                    </TableCell>
                    <TableCell
                      style={{ fontWeight: 'bold', minWidth: 200, width: 200 }}
                    >
                      Created On
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ height: 300 }}></TableCell>
                    </TableRow>
                  ) : tripData.length > 0 ? (
                    tripData.map((row) => (
                      <TableRow key={row.tripId}>
                        <TableCell>{row.tripId}</TableCell>
                        <TableCell>{row.sourceName || 'N/A'}</TableCell>
                        <TableCell>{row.destName || 'N/A'}</TableCell>
                        <TableCell>{row.vehicleName || 'N/A'}</TableCell>
                        <TableCell>
                          {row.pkgWeightName ? `${row.pkgWeightName}` : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {row.pkgTypeName ? `${row.pkgTypeName}` : 'N/A'}
                        </TableCell>
                        <TableCell
                          sx={{
                            minWidth: 200,
                            width: 200,
                            verticalAlign: 'top',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.75,
                            }}
                          >
                            <Box>
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: 'text.secondary',
                                  display: 'block',
                                  mb: 0.25,
                                }}
                              >
                                Start
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.primary',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {formatToIST(row.startDate) || 'N/A'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: 'text.secondary',
                                  display: 'block',
                                  mb: 0.25,
                                }}
                              >
                                End
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.primary',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {formatToIST(row.endDate) || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status || 'N/A'}
                            color={getStatusColor(row.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {row.createdBy ? (
                            <>
                              <div>{row.createdBy}</div>
                              {row.phoneNo && (
                                <div
                                  style={{ fontSize: '0.8rem', color: '#666' }}
                                >
                                  {row.phoneNo}
                                </div>
                              )}
                            </>
                          ) : row.phoneNo ? (
                            <div style={{ fontSize: '0.8rem', color: '#666' }}>
                              {row.phoneNo}
                            </div>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell>
                          {row.createdOn ? formatToIST(row.createdOn) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={(event) => handleMenuOpen(event, row)}
                          >
                            <Settings />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        align="center"
                        sx={{ height: 300 }}
                      >
                        No trips found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 50, 100]}
              component="div"
              // Use total from API pagination so it reflects full dataset
              count={pagination.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              sx={{
                width: '100%',
                overflow: 'auto',
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: { xs: 1, sm: 2 },
                  paddingRight: { xs: 1, sm: 2 },
                  flexWrap: 'wrap',
                  gap: { xs: 1, sm: 2 },
                  minHeight: { xs: '52px', sm: '64px' },
                },
                '& .MuiTablePagination-selectLabel': {
                  margin: 0,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-displayedRows': {
                  margin: 0,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-select': {
                  marginRight: { xs: 1, sm: 2 },
                  marginLeft: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-actions': {
                  marginLeft: { xs: 0.5, sm: 1 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                },
                '& .MuiIconButton-root': {
                  padding: { xs: '4px', sm: '8px' },
                },
                '& .MuiTablePagination-input': {
                  marginRight: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-menuItem': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
              }}
            />
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              <DropdownItem
                onClick={() => {
                  handleModalOpen();
                  handleMenuClose();
                }}
              >
                View Details
              </DropdownItem>
            </Menu>
          </Box>
        </CardContent>
      </Card>

      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(6px)',
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '94%', sm: 480 },
            maxWidth: 480,
            maxHeight: '85vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow:
              '0 32px 64px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.04)',
          }}
        >
          {selectedTrip && (
            <>
              {/* Header — branded */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 32,
                      borderRadius: 2,
                      bgcolor: colors.swiftcarry[100],
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      color="text.primary"
                      sx={{ lineHeight: 1.25 }}
                    >
                      Trip #{selectedTrip.tripId}
                    </Typography>
                    <Chip
                      label={selectedTrip.status || 'N/A'}
                      color={getStatusColor(selectedTrip.status)}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 22,
                        mt: 0.5,
                      }}
                    />
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleModalClose}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'text.primary',
                    },
                  }}
                  aria-label="Close"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>

              {/* Route strip — source → destination */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  bgcolor: `${colors.swiftcarry[100]}12`,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <LocationOn
                  sx={{
                    fontSize: 18,
                    color: colors.swiftcarry[100],
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.primary"
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {selectedTrip.sourceName || 'N/A'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.swiftcarry[100],
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      →
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.primary"
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {selectedTrip.destName || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Content — scrollable cards */}
              <Box
                sx={{
                  overflowY: 'auto',
                  flex: 1,
                  minHeight: 0,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                {/* Schedule */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Schedule
                      sx={{ fontSize: 18, color: colors.swiftcarry[100] }}
                    />
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ letterSpacing: 0.5 }}
                    >
                      SCHEDULE
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '64px 1fr',
                      gap: 0.5,
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Start
                    </Typography>
                    <Typography variant="body2">
                      {formatToIST(selectedTrip.startDate) || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      End
                    </Typography>
                    <Typography variant="body2">
                      {formatToIST(selectedTrip.endDate) || 'N/A'}
                    </Typography>
                  </Box>
                </Paper>

                {/* Vehicle & Package */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <LocalShipping
                      sx={{ fontSize: 18, color: colors.swiftcarry[100] }}
                    />
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ letterSpacing: 0.5 }}
                    >
                      VEHICLE & PACKAGE
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '64px 1fr',
                      gap: 0.5,
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Vehicle
                    </Typography>
                    <Typography variant="body2">
                      {selectedTrip.vehicleName || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Package
                    </Typography>
                    <Typography variant="body2">
                      {selectedTrip.pkgWeightName && selectedTrip.pkgTypeName
                        ? `${selectedTrip.pkgWeightName} · ${selectedTrip.pkgTypeName}`
                        : selectedTrip.pkgWeightName ||
                          selectedTrip.pkgTypeName ||
                          'N/A'}
                      {selectedTrip.pkgWidth != null &&
                        selectedTrip.pkgHeight != null && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                            W: {selectedTrip.pkgWidth}, H:{' '}
                          {selectedTrip.pkgHeight}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Preferences
                    </Typography>
                    <Typography variant="body2">
                      {selectedTrip.otherPreference || '—'}
                    </Typography>
                  </Box>
                </Paper>

                {/* Created by */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Person
                      sx={{ fontSize: 18, color: colors.swiftcarry[100] }}
                    />
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ letterSpacing: 0.5 }}
                    >
                      CREATED BY
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '64px 1fr',
                      gap: 0.5,
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body2">
                      {selectedTrip.createdBy || '—'}
                    </Typography>
                    {selectedTrip.phoneNo && (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body2">
                          {selectedTrip.phoneNo}
                        </Typography>
                      </>
                    )}
                    {selectedTrip.createdOn && (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          Created
                        </Typography>
                        <Typography variant="body2">
                          {formatToIST(selectedTrip.createdOn)}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Paper>
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleModalClose}
                  sx={{
                    py: 1,
                    borderRadius: 2,
                    fontWeight: 600,
                    bgcolor: colors.swiftcarry[100],
                    '&:hover': {
                      bgcolor: colors.swiftcarry[100],
                      opacity: 0.9,
                    },
                  }}
                >
                  Close
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Modal>

      {/* Create Trip Modal */}
      <Modal
        open={createModalOpen}
        onClose={handleCreateModalClose}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(6px)',
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '94%', sm: 600 },
            maxWidth: 600,
            maxHeight: '90vh',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow:
              '0 32px 64px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.04)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 32,
                  borderRadius: 2,
                  bgcolor: colors.swiftcarry[100],
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="h6"
                fontWeight={700}
                color="text.primary"
                sx={{ lineHeight: 1.25 }}
              >
                Create New Trip
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleCreateModalClose}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                },
              }}
              aria-label="Close"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Grid container spacing={2}>
              {/* Source */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Source"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={newTrip.source}
                  onChange={(e) => {
                    setNewTrip({
                      ...newTrip,
                      source: e.target.value,
                    });
                    setTripErrors({ ...tripErrors, source: '' });
                  }}
                  error={!!tripErrors.source}
                  helperText={tripErrors.source}
                  placeholder="latitude, longitude"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>

              {/* Destination */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Destination"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={newTrip.destination}
                  onChange={(e) => {
                    setNewTrip({
                      ...newTrip,
                      destination: e.target.value,
                    });
                    setTripErrors({ ...tripErrors, destination: '' });
                  }}
                  error={!!tripErrors.destination}
                  helperText={tripErrors.destination}
                  placeholder="latitude, longitude"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>

              {/* Source Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Source Name"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={newTrip.source_name}
                  onChange={(e) => {
                    setNewTrip({
                      ...newTrip,
                      source_name: e.target.value,
                    });
                    setTripErrors({ ...tripErrors, source_name: '' });
                  }}
                  error={!!tripErrors.source_name}
                  helperText={tripErrors.source_name}
                  placeholder="where are you starting?"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <LocationOn sx={{ color: colors.swiftcarry[100] }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>

              {/* Destination Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Destination Name"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={newTrip.dest_name}
                  onChange={(e) => {
                    setNewTrip({
                      ...newTrip,
                      dest_name: e.target.value,
                    });
                    setTripErrors({ ...tripErrors, dest_name: '' });
                  }}
                  error={!!tripErrors.dest_name}
                  helperText={tripErrors.dest_name}
                  placeholder="where are you going?"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <LocationOn sx={{ color: colors.swiftcarry[100] }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>

              {/* Start Date */}
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Start Date & Time"
                    value={newTrip.start_date}
                    format="DD/MM/YYYY hh:mm A"
                    onChange={(newValue) => {
                      setNewTrip({
                        ...newTrip,
                        start_date: newValue,
                      });
                      setTripErrors({ ...tripErrors, start_date: '' });
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        error: !!tripErrors.start_date,
                        helperText: tripErrors.start_date,
                        placeholder: 'Select Start Date & Time',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                              borderColor: colors.swiftcarry[100],
                              borderWidth: '2px',
                            },
                            '&:hover fieldset': {
                              borderColor: colors.swiftcarry[100],
                            },
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: colors.swiftcarry[100],
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* End Date */}
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="End Date & Time"
                    value={newTrip.end_date}
                    format="DD/MM/YYYY hh:mm A"
                    onChange={(newValue) => {
                      setNewTrip({
                        ...newTrip,
                        end_date: newValue,
                      });
                      setTripErrors({ ...tripErrors, end_date: '' });
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        error: !!tripErrors.end_date,
                        helperText: tripErrors.end_date,
                        placeholder: 'Select End Date & Time',
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                              borderColor: colors.swiftcarry[100],
                              borderWidth: '2px',
                            },
                            '&:hover fieldset': {
                              borderColor: colors.swiftcarry[100],
                            },
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: colors.swiftcarry[100],
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* User ID */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="User ID"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={newTrip.userId}
                  onChange={(e) => {
                    setNewTrip({
                      ...newTrip,
                      userId: e.target.value,
                    });
                    setTripErrors({ ...tripErrors, userId: '' });
                  }}
                  error={!!tripErrors.userId}
                  helperText={tripErrors.userId || ''}
                  placeholder="Enter User ID"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>

              {/* Vehicle Details */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Vehicle Details"
                  variant="outlined"
                  fullWidth
                  size="small"
                  type="number"
                  value={newTrip.vehicle_details}
                  onChange={(e) => {
                    setNewTrip({
                      ...newTrip,
                      vehicle_details: parseInt(e.target.value) || null,
                    });
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>

              {/* Package Size (Dimensions) - uses backend package weight IDs */}
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ mb: 1, color: 'text.primary' }}
                >
                  Carrying Capacity
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {(() => {
                    const dimensionPresets = [
                      { w: 1, h: 1, d: 1 },
                      { w: 2, h: 2, d: 2 },
                      { w: 3, h: 3, d: 3 },
                      { w: 5, h: 5, d: 5 },
                    ];
                    const iconList = [Work, Work, Luggage, BusinessCenter];
                    const uiLabels = ['XS', 'Small', 'Medium', 'Large'];
                    const weightLabels = [
                      'up to 500g',
                      'up to 2kg',
                      'up to 5kg',
                      'up to 10kg+',
                    ];
                    const namePatterns = [
                      /^(xs|extra\s*small)$/i,
                      /^(s|small)$/i,
                      /^(m|medium)$/i,
                      /^(l|large)$/i,
                    ];
                    const sorted = [...(packageWeights || [])].sort(
                      (a, b) =>
                        (a.orderId ?? a.order ?? 0) -
                        (b.orderId ?? b.order ?? 0),
                    );
                    const capacityOptions = uiLabels.map((label, i) => {
                      const pw =
                        sorted.find((p) =>
                          namePatterns[i].test(
                            p.packageWeightName ?? p.package_weight_name ?? '',
                          ),
                        ) ?? sorted[i];
                      const weightId =
                        pw?.packageWeightId ?? pw?.package_weight_id;
                      if (weightId == null) return null;
                      const dims = dimensionPresets[i] ?? {
                        w: Math.min(i + 1, 5),
                        h: Math.min(i + 1, 5),
                        d: Math.min(i + 1, 5),
                      };
                      return {
                        value: String(weightId),
                        icon: iconList[i] || Work,
                        label: uiLabels[i],
                        width: dims.w,
                        height: dims.h,
                        depth: dims.d,
                        weight: weightId,
                        weightDescription: weightLabels[i] ?? '',
                      };
                    }).filter(Boolean);
                    return capacityOptions.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Loading package weights…
                      </Typography>
                    ) : (
                      capacityOptions.map((size) => (
                        <Paper
                          key={size.value}
                          onClick={() => {
                            setSelectedCapacity(size.value);
                            setNewTrip((prev) => ({
                              ...prev,
                              pkg_width: size.width,
                              pkg_height: size.height,
                              pkg_depth: size.depth,
                              pkg_weight: size.weight,
                            }));
                          }}
                          sx={{
                            flex: '1 1 calc(25% - 12px)',
                            minWidth: '100px',
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            borderRadius: '12px',
                            border: '2px solid',
                            borderColor:
                              selectedCapacity === size.value
                                ? colors.swiftcarry[100]
                                : 'divider',
                            bgcolor:
                              selectedCapacity === size.value
                                ? `${colors.swiftcarry[100]}15`
                                : 'background.paper',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: colors.swiftcarry[100],
                              bgcolor: `${colors.swiftcarry[100]}08`,
                            },
                          }}
                        >
                          <size.icon
                            sx={{
                              fontSize: 28,
                              color:
                                selectedCapacity === size.value
                                  ? colors.swiftcarry[100]
                                  : 'text.secondary',
                            }}
                          />
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{
                              color:
                                selectedCapacity === size.value
                                  ? colors.swiftcarry[100]
                                  : 'text.primary',
                            }}
                          >
                            {size.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {size.width}×{size.height}×{size.depth} ft
                          </Typography>
                          {size.weightDescription ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: '0.7rem' }}
                            >
                              {size.weightDescription}
                            </Typography>
                          ) : null}
                        </Paper>
                      ))
                    );
                  })()}
                </Box>
              </Grid>

              {/* Parcel Preference */}
              {/* <Grid item xs={12}>
                <TextField
                  label="Parcel Preference"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={newTrip.parcel_preference}
                  onChange={(e) => {
                    setNewTrip({
                      ...newTrip,
                      parcel_preference: e.target.value,
                    });
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>*/}

              {/* Trip Count */}
              <Grid item xs={12}>
                <TextField
                  label="Trip Count"
                  variant="outlined"
                  fullWidth
                  size="small"
                  type="number"
                  value={newTrip.trip_count}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === '' ||
                      (/^\d+$/.test(value) && parseInt(value) > 0)
                    ) {
                      setNewTrip({
                        ...newTrip,
                        trip_count: value,
                      });
                    }
                  }}
                  placeholder="Enter Trip Count"
                  inputProps={{ min: 1 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>

              {/* Route */}
              {/* <Grid item xs={12} sm={6}>
                <TextField
                  label="Route"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={newTrip.route}
                  onChange={(e) => {
                    setNewTrip({
                      ...newTrip,
                      route: e.target.value,
                    });
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: "2px",
                      },
                      "&:hover fieldset": {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>

              {/* Other Preference */}
              {/*    <Grid item xs={12}>
                <TextField
                  label="Other Preference"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={newTrip.other_preference}
                  onChange={(e) => {
                    setNewTrip({
                      ...newTrip,
                      other_preference: e.target.value,
                    });
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>*/}
            </Grid>
          </Box>

          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCreateModalClose}
              disabled={createLoading}
              sx={{
                borderColor: '#ccc',
                color: '#666',
                '&:hover': {
                  borderColor: colors.swiftcarry[100],
                  color: colors.swiftcarry[100],
                  backgroundColor: `${colors.swiftcarry[100]}10`,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleCreateTrip}
              disabled={createLoading}
              sx={{
                py: 1,
                borderRadius: 2,
                fontWeight: 600,
                bgcolor: colors.swiftcarry[100],
                '&:hover': {
                  bgcolor: colors.swiftcarry[100],
                  opacity: 0.9,
                },
                '&:disabled': {
                  bgcolor: '#ccc',
                  color: '#fff',
                },
              }}
            >
              {createLoading ? <CircularProgress size={20} /> : 'Create Trip'}
            </Button>
          </Box>
        </Paper>
      </Modal>
    </>
  );
};

export default TripManagement;
